/**
 * achievementData.ts — Achievement dataset + reward mapping (mascot domain SSOT).
 *
 * DDD note: this data previously lived inside the UI component
 * `components/progress/AchievementsSection.tsx`, which forced the mascot domain
 * (`useDjayCustomization`) to import a UI component — an inverted dependency
 * flagged in MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §4.2 and DJAY_MASCOT_DOMAIN_2026
 * §3.7. The pure data + types now live here, in the mascot domain. The UI
 * component re-exports these for its presentational consumers.
 *
 * Reward model (curated reward-cards): each cosmetic (accessory or variant) is
 * granted by exactly ONE achievement card. Every other card carries
 * `accessory: 'none'` and no `djayVariantUnlocked` — it is a pure achievement
 * card; the card itself is the unlockable. See DJAY_MASCOT_DOMAIN_2026 §3.2.
 */
import type { DjayEmotion, DjayVariant, DjayAccessory } from '../DjayAvatar';
import { MODULES } from '../../content';
import { DJAY_THRESHOLDS } from '../config';
export type AchievementRarity = 'common' | 'uncommon' | 'rare' | 'legendary';
export interface Achievement {
  id: string;
  name: string;
  description: string;
  emoji: string;
  category: 'onboarding' | 'streak' | 'mastery' | 'level' | 'special';
  rarity: AchievementRarity;
  djayEmotionUnlocked: DjayEmotion;
  djayEmotionLocked: DjayEmotion;
  djayVariantUnlocked?: DjayVariant;
  accessory: DjayAccessory;
  check: (data: AchievementCheckData) => boolean;
  getProgress?: (data: AchievementCheckData) => {
    current: number;
    max: number;
    label: string;
  };
}
export interface AchievementCheckData {
  totalQuestionsAnswered: number;
  masteredCount: number;
  level: number;
  xp: number;
  // Dagelijkse streak (§15)
  currentWeekDays: number; // 0-5, schooldagen deze week
  consecutiveFullWeeks: number; // Opeenvolgende volle weken
  // Antwoord-streak (§14)
  longestTotalStreak: number; // Persoonlijk record correct-op-rij
  // Dagelijks doel
  totalDailyGoalsReached: number; // Lifetime count
  // Perfecte sessies
  totalPerfectSessions: number; // Lifetime count
  // Leer-hub (Oefenlab) gebruik — drijft de usage-earned treatment-finishes.
  // Optioneel zodat bestaande check-callers ongewijzigd blijven; defaults 0.
  labSessionsCompleted?: number;
}
export const ACHIEVEMENTS: Achievement[] = [
// Eerste Stappen (Onboarding)
{
  id: 'first-steps',
  name: 'Eerste Stappen',
  description: 'Beantwoord 5 vragen',
  emoji: '🌱',
  category: 'onboarding',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'sleeping',
  accessory: 'seedling',
  check: data => data.totalQuestionsAnswered >= 5,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 5),
    max: 5,
    label: 'vragen'
  })
}, {
  id: 'getting-started',
  name: 'Lekker Bezig',
  description: 'Beantwoord 50 vragen',
  emoji: '📝',
  category: 'onboarding',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'pencil',
  check: data => data.totalQuestionsAnswered >= 50,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 50),
    max: 50,
    label: 'vragen'
  })
}, {
  id: 'century',
  name: 'Honderdtal',
  description: 'Beantwoord 100 vragen',
  emoji: '💯',
  category: 'onboarding',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'pixel',
  accessory: 'badge100',
  // Threshold shared with the `pixel` variant unlock (useVariantUnlocks) via
  // the central DJAY_THRESHOLDS SSOT — the achievement card and the variant
  // grid can never drift apart. (audit §9.4 hardening)
  check: data => data.totalQuestionsAnswered >= DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED),
    max: DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED,
    label: 'vragen'
  })
}, {
  id: 'quarter-thousand',
  name: 'Kwart Duizend',
  description: 'Beantwoord 250 vragen',
  emoji: '🎯',
  category: 'onboarding',
  rarity: 'rare',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.totalQuestionsAnswered >= 250,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 250),
    max: 250,
    label: 'vragen'
  })
}, {
  id: 'half-thousand',
  name: 'Halve Duizend',
  description: 'Beantwoord 500 vragen',
  emoji: '🚀',
  category: 'onboarding',
  rarity: 'legendary',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  accessory: 'starWand',
  check: data => data.totalQuestionsAnswered >= 500,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 500),
    max: 500,
    label: 'vragen'
  })
}, {
  id: 'millennium',
  name: 'Duizendklapper',
  description: 'Beantwoord 1000 vragen',
  emoji: '🎆',
  category: 'onboarding',
  rarity: 'legendary',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'sleeping',
  accessory: 'tripleStars',
  check: data => data.totalQuestionsAnswered >= 1000,
  getProgress: data => ({
    current: Math.min(data.totalQuestionsAnswered, 1000),
    max: 1000,
    label: 'vragen'
  })
},
// Reeksen (Streaks)
{
  id: 'three-days',
  name: 'Drie Dagen',
  description: 'Oefen 3 schooldagen deze week',
  emoji: '🔥',
  category: 'streak',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'sleeping',
  accessory: 'smallFlame',
  check: data => data.currentWeekDays >= 3,
  getProgress: data => ({
    current: Math.min(data.currentWeekDays, 3),
    max: 3,
    label: 'dagen'
  })
}, {
  id: 'week-champion',
  name: 'Week Kampioen',
  description: 'Oefen een hele schoolweek (5/5)',
  emoji: '🏆',
  category: 'streak',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'trophy',
  check: data => data.currentWeekDays >= 5,
  getProgress: data => ({
    current: Math.min(data.currentWeekDays, 5),
    max: 5,
    label: 'dagen'
  })
}, {
  id: 'fire-master',
  name: 'Vuurmeester',
  description: 'Oefen 3 volle weken achter elkaar',
  emoji: '🌋',
  category: 'streak',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'blaze',
  accessory: 'doubleFlameCrown',
  // Threshold shared with the `blaze` variant unlock via DJAY_THRESHOLDS.
  check: data => data.consecutiveFullWeeks >= DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS,
  getProgress: data => ({
    current: Math.min(data.consecutiveFullWeeks, DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS),
    max: DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS,
    label: 'weken'
  })
}, {
  id: 'hot-streak',
  name: 'Op Dreef',
  description: '10 correct op rij',
  emoji: '⚡',
  category: 'streak',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.longestTotalStreak >= 10,
  getProgress: data => ({
    current: Math.min(data.longestTotalStreak, 10),
    max: 10,
    label: 'op rij'
  })
}, {
  id: 'two-weeks',
  name: 'Twee Weken',
  description: 'Oefen 2 volle weken achter elkaar',
  emoji: '📅',
  category: 'streak',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.consecutiveFullWeeks >= 2,
  getProgress: data => ({
    current: Math.min(data.consecutiveFullWeeks, 2),
    max: 2,
    label: 'weken'
  })
}, {
  id: 'unstoppable',
  name: 'Onstopbaar',
  description: '25 correct op rij',
  emoji: '🌟',
  category: 'streak',
  rarity: 'rare',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.longestTotalStreak >= 25,
  getProgress: data => ({
    current: Math.min(data.longestTotalStreak, 25),
    max: 25,
    label: 'op rij'
  })
}, {
  id: 'legendary-streak',
  name: 'Legendarische Reeks',
  description: '30 correct op rij',
  emoji: '👑',
  category: 'streak',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'scepter',
  check: data => data.longestTotalStreak >= 30,
  getProgress: data => ({
    current: Math.min(data.longestTotalStreak, 30),
    max: 30,
    label: 'op rij'
  })
}, {
  id: 'unbreakable',
  name: 'Onbreekbaar',
  description: '10 volle weken achter elkaar',
  emoji: '💎',
  category: 'streak',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.consecutiveFullWeeks >= 10,
  getProgress: data => ({
    current: Math.min(data.consecutiveFullWeeks, 10),
    max: 10,
    label: 'weken'
  })
},
// Meesterschap (Mastery)
{
  id: 'first-subject',
  name: 'Eerste Vak Klaar',
  description: 'Rond je eerste vak af',
  emoji: '⭐',
  category: 'mastery',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'encouraging',
  accessory: 'none',
  check: data => data.masteredCount >= 1,
  getProgress: data => ({
    current: Math.min(data.masteredCount, 1),
    max: 1,
    label: 'vak'
  })
}, {
  id: 'three-subjects',
  name: 'Drie Vakken',
  description: 'Rond 3 vakken af',
  emoji: '🌟',
  category: 'mastery',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'pointing',
  djayVariantUnlocked: 'frost',
  accessory: 'none',
  // Threshold shared with the `frost` variant unlock via DJAY_THRESHOLDS.
  check: data => data.masteredCount >= DJAY_THRESHOLDS.FROST_MODULES_MASTERED,
  getProgress: data => ({
    current: Math.min(data.masteredCount, DJAY_THRESHOLDS.FROST_MODULES_MASTERED),
    max: DJAY_THRESHOLDS.FROST_MODULES_MASTERED,
    label: 'vakken'
  })
}, {
  id: 'all-subjects',
  name: 'Alle Vakken',
  description: `Rond alle ${MODULES.length} vakken af`,
  emoji: '👑',
  category: 'mastery',
  rarity: 'legendary',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'royal',
  accessory: 'none',
  check: data => data.masteredCount >= MODULES.length,
  getProgress: data => ({
    current: Math.min(data.masteredCount, MODULES.length),
    max: MODULES.length,
    label: 'vakken'
  })
},
// Levels
{
  id: 'level-3',
  name: 'Beginner',
  description: 'Bereik level 3',
  emoji: '🌱',
  category: 'level',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'sleeping',
  accessory: 'none',
  check: data => data.level >= 3,
  getProgress: data => ({
    current: Math.min(data.level, 3),
    max: 3,
    label: 'levels'
  })
}, {
  id: 'level-5',
  name: 'Kenner',
  description: 'Bereik level 5',
  emoji: '🌿',
  category: 'level',
  rarity: 'common',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'leafGarland',
  check: data => data.level >= 5,
  getProgress: data => ({
    current: Math.min(data.level, 5),
    max: 5,
    label: 'levels'
  })
}, {
  id: 'level-7',
  name: 'Gevorderd',
  description: 'Bereik level 7',
  emoji: '🌳',
  category: 'level',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.level >= 7,
  getProgress: data => ({
    current: Math.min(data.level, 7),
    max: 7,
    label: 'levels'
  })
}, {
  id: 'level-10',
  name: 'Kampioen',
  description: 'Bereik level 10',
  emoji: '🌟',
  category: 'level',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  accessory: 'medal',
  check: data => data.level >= 10,
  getProgress: data => ({
    current: Math.min(data.level, 10),
    max: 10,
    label: 'levels'
  })
}, {
  id: 'level-12',
  name: 'Expert',
  description: 'Bereik level 12',
  emoji: '🎓',
  category: 'level',
  rarity: 'rare',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.level >= 12,
  getProgress: data => ({
    current: Math.min(data.level, 12),
    max: 12,
    label: 'levels'
  })
}, {
  id: 'level-15',
  name: 'Ultieme Meester',
  description: 'Bereik level 15',
  emoji: '🌌',
  category: 'level',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'sleeping',
  djayVariantUnlocked: 'cosmic',
  accessory: 'cosmicOrb',
  // Threshold shared with the `cosmic` variant unlock via DJAY_THRESHOLDS.
  check: data => data.level >= DJAY_THRESHOLDS.COSMIC_LEVEL,
  getProgress: data => ({
    current: Math.min(data.level, DJAY_THRESHOLDS.COSMIC_LEVEL),
    max: DJAY_THRESHOLDS.COSMIC_LEVEL,
    label: 'levels'
  })
},
// Special
{
  id: 'shadow-realm',
  name: 'Schaduw Meester',
  description: 'Behaal 30.000 XP',
  emoji: '🌑',
  category: 'special',
  rarity: 'legendary',
  djayEmotionUnlocked: 'surprised',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'shadow',
  accessory: 'shadowOrb',
  // Threshold shared with the `shadow` variant unlock via DJAY_THRESHOLDS.
  check: data => data.xp >= DJAY_THRESHOLDS.SHADOW_XP,
  getProgress: data => ({
    current: Math.min(data.xp, DJAY_THRESHOLDS.SHADOW_XP),
    max: DJAY_THRESHOLDS.SHADOW_XP,
    label: 'XP'
  })
}, {
  id: 'goal-getter',
  name: 'Doelpakker',
  description: 'Bereik 10x je dagelijks doel',
  emoji: '🎯',
  category: 'special',
  rarity: 'common',
  djayEmotionUnlocked: 'happy',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.totalDailyGoalsReached >= 10,
  getProgress: data => ({
    current: Math.min(data.totalDailyGoalsReached, 10),
    max: 10,
    label: 'keer'
  })
}, {
  id: 'consistent',
  name: 'Consistent',
  description: 'Bereik 50x je dagelijks doel',
  emoji: '📅',
  category: 'special',
  rarity: 'uncommon',
  djayEmotionUnlocked: 'excited',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.totalDailyGoalsReached >= 50,
  getProgress: data => ({
    current: Math.min(data.totalDailyGoalsReached, 50),
    max: 50,
    label: 'keer'
  })
}, {
  id: 'daily-master',
  name: 'Dagelijkse Meester',
  description: 'Bereik 100x je dagelijks doel',
  emoji: '🎯',
  category: 'special',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.totalDailyGoalsReached >= 100,
  getProgress: data => ({
    current: Math.min(data.totalDailyGoalsReached, 100),
    max: 100,
    label: 'keer'
  })
}, {
  id: 'perfectionist',
  name: 'Perfectionist',
  description: '5 perfecte sessies',
  emoji: '🏆',
  category: 'special',
  rarity: 'rare',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.totalPerfectSessions >= 5,
  getProgress: data => ({
    current: Math.min(data.totalPerfectSessions, 5),
    max: 5,
    label: 'sessies'
  })
}, {
  id: 'flawless',
  name: 'Foutloos',
  description: '10 perfecte sessies',
  emoji: '✨',
  category: 'special',
  rarity: 'rare',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.totalPerfectSessions >= 10,
  getProgress: data => ({
    current: Math.min(data.totalPerfectSessions, 10),
    max: 10,
    label: 'sessies'
  })
}, {
  id: 'untouchable',
  name: 'Onaantastbaar',
  description: '25 perfecte sessies',
  emoji: '💎',
  category: 'special',
  rarity: 'legendary',
  djayEmotionUnlocked: 'proud',
  djayEmotionLocked: 'thinking',
  accessory: 'none',
  check: data => data.totalPerfectSessions >= 25,
  getProgress: data => ({
    current: Math.min(data.totalPerfectSessions, 25),
    max: 25,
    label: 'sessies'
  })
}, {
  id: 'rainbow-djay',
  name: 'Regenboog Verzamelaar',
  description: 'Verzamel alle 6 leer-mascottes (Pixel, Blaze, Frost, Royal, Cosmic, Shadow)',
  emoji: '🌈',
  category: 'special',
  rarity: 'legendary',
  djayEmotionUnlocked: 'celebrating',
  djayEmotionLocked: 'thinking',
  djayVariantUnlocked: 'rainbow',
  accessory: 'none',
  // All five clauses read the central DJAY_THRESHOLDS SSOT, so this card and
  // the `rainbow` variant unlock (useVariantUnlocks) share one source of
  // truth for every threshold. (audit §9.4 hardening)
  check: data => data.totalQuestionsAnswered >= DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED && data.consecutiveFullWeeks >= DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS && data.masteredCount >= MODULES.length && data.level >= DJAY_THRESHOLDS.COSMIC_LEVEL && data.xp >= DJAY_THRESHOLDS.SHADOW_XP,
  getProgress: data => {
    // Each entry MUST mirror exactly one clause of check() so progress can
    // never read 6/6 while the achievement is still locked (audit P0 fix).
    const unlocked = [data.totalQuestionsAnswered >= DJAY_THRESHOLDS.PIXEL_QUESTIONS_ANSWERED, data.consecutiveFullWeeks >= DJAY_THRESHOLDS.BLAZE_CONSECUTIVE_FULL_WEEKS, data.masteredCount >= MODULES.length, data.level >= DJAY_THRESHOLDS.COSMIC_LEVEL, data.xp >= DJAY_THRESHOLDS.SHADOW_XP].filter(Boolean).length;
    return {
      current: unlocked,
      max: 5,
      label: 'mascottes'
    };
  }
}];
export const CATEGORY_LABELS: Record<Achievement['category'], string> = {
  onboarding: 'Eerste Stappen',
  streak: 'Reeksen',
  mastery: 'Meesterschap',
  level: 'Levels',
  special: 'Speciaal'
};
export const RARITY_CONFIG: Record<AchievementRarity, {
  label: string;
  cardBg: string;
  cardBorder: string;
  glowColor: string;
  textColor: string;
  badgeBg: string;
  badgeText: string;
}> = {
  common: {
    label: 'Gewoon',
    cardBg: 'bg-white',
    cardBorder: 'border-slate-200',
    glowColor: '',
    textColor: 'text-slate-600',
    badgeBg: 'bg-slate-100',
    badgeText: 'text-slate-500'
  },
  uncommon: {
    label: 'Ongewoon',
    cardBg: 'bg-gradient-to-br from-emerald-50 to-teal-50',
    cardBorder: 'border-emerald-200',
    glowColor: 'shadow-emerald-200/50',
    textColor: 'text-emerald-700',
    badgeBg: 'bg-emerald-100',
    badgeText: 'text-emerald-600'
  },
  rare: {
    label: 'Zeldzaam',
    cardBg: 'bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50',
    cardBorder: 'border-amber-300',
    glowColor: 'shadow-amber-300/60',
    textColor: 'text-amber-700',
    badgeBg: 'bg-amber-100',
    badgeText: 'text-amber-700'
  },
  legendary: {
    label: 'Legendarisch',
    cardBg: 'bg-gradient-to-br from-purple-50 via-fuchsia-50 to-pink-50',
    cardBorder: 'border-purple-300',
    glowColor: 'shadow-purple-400/50',
    textColor: 'text-purple-700',
    badgeBg: 'bg-gradient-to-r from-purple-500 to-fuchsia-500',
    badgeText: 'text-white'
  }
};